import { AssetVisitYardEventType } from './asset-visit';
import { DatabaseEntities, INovaEntity } from './base';
import { Maybe } from './globals';

export enum YardViewModeEnum {
  GRID = 'grid',
  LIST = 'list',
  TV = 'tv'
}

export enum YardTasksViewModeEnum {
  BOARD = 'board',
  LIST = 'list'
}

export enum SpotType {
  PARKING = 'parking',
  DOCKING = 'docking'
}

export enum SpotStatus {
  OPEN = 'open',
  CLOSED = 'closed',
  ASSIGNED = 'assigned',
  RESERVED_MANUAL = 'reserved_manual',
  RESERVED_AUTOMATED = 'reserved_automated',
  LOCKED = 'locked',
  UNKNOWN = 'unknown',

  /**
   * @deprecated
   * Use RESERVED_MANUAL or RESERVED_AUTOMATED
   */
  RESERVED = 'reserved'
}

export enum SpotEventTypeEnum {
  ASSIGNMENT = 'assignment',
  RELEASE = 'release',
  RESERVE = 'reserve',
  RELEASE_RESERVE = 'release_reserve',
  CLOSE = 'close',
  OPEN = 'open',
  OTHER = 'other'
}

export enum SpotReserveEntityEnum {
  YardTask = DatabaseEntities.YardTask
}

export interface ISpot {
  id: string;
  code: string;
  type: SpotType;
  isActive: boolean;
  status?: SpotStatus;
  spotAssignments?: ISpotAssignment[];
  spotReserves?: ISpotReserve[];
  /** Yard-view BFF: active move task when present (spot grid / detail / list) */
  openMoveTask?: Maybe<{ id: string; refNum?: Maybe<string> }>;
}

export interface ISpotAssignment extends INovaEntity {
  id: string;
  spotId: string;
  warehouseId: string;
  assetVisitId?: Maybe<string>;
  assetContainerId?: Maybe<string>;
}

export interface ISpotReserve extends INovaEntity {
  id: string;
  spotId: string;
  warehouseId: string;
  objectId: string;
  entityName: SpotReserveEntityEnum;
}

export enum DepartTypeEnum {
  All = 'All',
  AssetVisit = DatabaseEntities.AssetVisit,
  AssetContainer = DatabaseEntities.AssetContainer
}

export function getSpotStatus(spot: ISpot): SpotStatus {
  if (spot.status && spot.status !== SpotStatus.UNKNOWN) {
    return spot.status;
  }

  const spotReserves = spot?.spotReserves || [];
  const spotAssignments = spot?.spotAssignments || [];

  const hasActiveSpotReserves = spotReserves.some(reserve => reserve.isActive);

  if (spot.isActive === false && hasActiveSpotReserves) {
    return SpotStatus.RESERVED;
  }

  if (spot.isActive === false) {
    return SpotStatus.CLOSED;
  }

  if (spot.isActive === true && !spotAssignments.length) {
    return SpotStatus.OPEN;
  }

  if (spot.isActive === true && spotAssignments.length) {
    return SpotStatus.ASSIGNED;
  }

  return SpotStatus.UNKNOWN;
}

export function isSpotAssignedToAssetVisit(spot: ISpot): boolean {
  return spot.spotAssignments?.some(assignment => Boolean(assignment.assetVisitId)) ?? false;
}

export function isSpotAssignedOnlyToAssetVisit(spot: ISpot): boolean {
  return spot.spotAssignments?.every(assignment => Boolean(assignment.assetVisitId)) ?? false;
}

export function isSpotAssignedToAssetContainer(spot: ISpot): boolean {
  return spot.spotAssignments?.some(assignment => Boolean(assignment.assetContainerId)) ?? false;
}

export function isSpotAssignedOnlyToAssetContainer(spot: ISpot): boolean {
  return spot.spotAssignments?.every(assignment => Boolean(assignment.assetContainerId)) ?? false;
}

/*
 * Sorts the spot list by spot code using Natural Sorting algorithm, IN-PLACE
 */
export function sortSpots(spots: ISpot[]): void {
  const extractNumber = (code: string) => {
    const match = code.match(/\d+/u);
    return match ? parseInt(match[0], 10) : Infinity;
  };

  const extractAlpha = (code: string) => {
    const match = code.match(/[a-zA-Z]+/u);
    return match ? match[0] : '';
  };

  if (
    Array.isArray(spots) &&
    spots.every(spot => typeof spot === 'object' && spot !== null && 'code' in spot)
  ) {
    spots.sort((a, b) => {
      const alphaA = extractAlpha(a.code);
      const alphaB = extractAlpha(b.code);
      if (alphaA === alphaB) {
        return extractNumber(a.code) - extractNumber(b.code);
      }
      return alphaA.localeCompare(alphaB);
    });
  }
}

export const YARD_SPOT_CODE_MAX_LENGTH = 5;

export function getSpotEventType(
  eventType: SpotEventTypeEnum,
  spotType: SpotType
): AssetVisitYardEventType | undefined {
  if (eventType === SpotEventTypeEnum.ASSIGNMENT) {
    if (spotType === SpotType.DOCKING) {
      return AssetVisitYardEventType.Docked;
    }
    return AssetVisitYardEventType.Parked;
  } else if (eventType === SpotEventTypeEnum.RELEASE) {
    return AssetVisitYardEventType.Unassigned;
  }
}
